export interface FilterSuggestion {
  id: number;
  criteria: Record<string, any>;
  label: string;
  // Measured by the server when the filter was proposed. null means "not
  // measured", not "no hits"; capped means the index counts no further.
  total?: number | null;
  capped?: boolean;
  created: string;
}

export interface WorkspaceMessage {
  id?: number;
  role: "user" | "assistant";
  content: string;
  finished: boolean;
  created?: string;
  filters: FilterSuggestion[];
  researchProposals: ResearchProposal[];
  reportProposals: ReportProposal[];
  skillRuns: SkillRun[];
}

export type SkillFieldType = "parcel" | "parcels" | "text" | "choice" | "multi_choice" | "range";

export interface SkillOption {
  value: string;
  label: string;
}

export interface SkillField {
  name: string;
  label: string;
  type: SkillFieldType;
  required: boolean;
  default?: any;
  options: SkillOption[];
  description: string;
  maxLength: number;
  // Een keuze die andere velden zet: {waarde: {veld: waarde}}.
  sets?: Record<string, Record<string, any>>;
}

// Wat een skill in de lijst en op zijn kaart herkenbaar maakt.
export interface SkillMarks {
  // Gemaakt door de organisatie, niet door het systeem.
  organization?: boolean;
  // Een van zijn acties kost geld; het bedrag wordt eerst bevestigd.
  paid?: boolean;
}

export interface SkillDefinition extends SkillMarks {
  name: string;
  label: string;
  // Wat het formulier vraagt: de eigen velden, of die van de ene actie.
  fields: SkillField[];
  // Een skill met instructie: Start is een beurt van het model.
  turn?: boolean;
  updatedBy?: string;
}

export type SkillRunStatus = "proposed" | "started" | "failed";

export type ActionRunStatus = "pending" | "running" | "done" | "failed" | "cancelled";

// Eén uitvoering van een actie, vanuit een formulier of door het model in een
// skillbeurt. De meeste zijn meteen klaar; een wacht op het afgemaakte antwoord
// (document opslaan) of op een klik (betaald).
export interface ActionRun {
  id: number;
  action: string;
  label: string;
  paid: boolean;
  status: ActionRunStatus;
  arguments: Record<string, any>;
  costEur?: string | null;
  result: Record<string, any>;
  // Het record dat de actie maakte.
  filterId?: number | null;
  researchId?: number | null;
  reportId?: number | null;
  fileName?: string | null;
  fileUrl?: string | null;
  error: string;
}

// Eén keer dat een skill is aangeboden of gestart, met de velden zoals ze toen
// waren en de waarden waarmee hij is ingevuld.
export interface SkillRun extends SkillMarks {
  id: number;
  messageId: number;
  skill: string;
  label: string;
  fields: SkillField[];
  values: Record<string, any>;
  status: SkillRunStatus;
  error: string;
  turn?: boolean;
  actions?: ActionRun[];
  // Runs van vóór elke uitvoering een ActionRun was: hun record.
  filterId?: number | null;
  researchProposalId?: number | null;
  reportId?: number | null;
  created: string;
}

// Het model stelde een gespreksrapport voor; `fileUrl` is er zodra de PDF is gemaakt.
export interface ReportProposal {
  id: number;
  title: string;
  situation: string;
  conclusion: string;
  fileId?: number | null;
  fileUrl?: string | null;
  // "" (nooit gevraagd), queued, running, done, failed — de PDF wordt in een
  // Celery-taak gemaakt; zolang hij queued/running is pollt de kaart.
  pdfStatus?: PdfStatus;
  pdfError?: string;
  created: string;
}

export type PdfStatus = "" | "queued" | "running" | "done" | "failed";

export const pdfPending = (status?: PdfStatus) => status === "queued" || status === "running";

export interface ResearchProposal {
  id: number;
  parcelId: number;
  parcelName: string;
  extraQuestion: string;
  researchId?: number | null;
  created: string;
}

export interface ChatSessionSummary {
  id: number;
  title: string;
  created: string;
  modified: string;
  messageCount: number;
}

export interface ChatSessionDetail extends ChatSessionSummary {
  messages: WorkspaceMessage[];
}
